/**
 * Per-device persistence of "this user has already passed the on-site
 * QR / 6-digit location check at this location" — used by the
 * equipmentRental useOrder flow to verify location once per usage,
 * keyed by `locationId`, instead of once per ticket / flow entry.
 *
 * The static 6-digit code (and the QR it encodes) is reusable, so
 * re-entering it on every flow entry adds friction without security
 * value (see plan D2). One device = one check satisfies "one
 * identifiable user, one location check" — a booking loaded on a
 * different phone has no record and re-verifies.
 */
const STORAGE_KEY = "kolula:location-verifications";

type LocationVerificationRecord = Record<string, number>;

function read(): LocationVerificationRecord {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write(record: LocationVerificationRecord): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore — storage unavailable (e.g. private mode, quota)
  }
}

export function isLocationVerified(
  locationId: string | undefined | null
): boolean {
  if (!locationId) return false;
  const record = read();
  return Boolean(record[locationId]);
}

export function setLocationVerified(locationId: string): void {
  if (!locationId) return;
  const record = read();
  record[locationId] = Date.now();
  write(record);
}

export function clearLocationVerification(locationId: string): void {
  if (!locationId) return;
  const record = read();
  if (record[locationId] !== undefined) {
    delete record[locationId];
    write(record);
  }
}

/**
 * Drop records for any location that is not in the given active set.
 * Intended to be called when the active-booking list changes (e.g.,
 * after a rental finishes) so a no-longer-active location's stored
 * verification doesn't leak into a later booking on the same device.
 */
export function pruneLocationVerifications(
  activeLocationIds: ReadonlyArray<string>
): void {
  const record = read();
  const activeSet = new Set(activeLocationIds);
  let mutated = false;
  for (const locId of Object.keys(record)) {
    if (!activeSet.has(locId)) {
      delete record[locId];
      mutated = true;
    }
  }
  if (mutated) write(record);
}
